// src/prf/prfIntervalStatus.js
// Where a Rainfall Index interval stands, and what may be said about it.
//
// Kevin, 2026-11-02 (approving the open-interval fix): farmers see dollars only
// once an interval has closed, labeled "Estimate at close" until RMA publishes
// and "Final" after. While an interval is still open: "I somehow want to see
// something while the interval is still open, like % of normal rainfall. Doesn't
// need to have dollars but should be able to calculate percent of normal to date
// within that interval".
//
// Why the rule exists: prf_grid_indices.estimated_index_value is rain so far ÷
// the WHOLE interval's normal. Mid-interval that reads low by however much of
// the interval is still to come, and every open interval looked like a trigger:
// measured 2026-11-01, 58 of 139 open grid-intervals read below trigger and the
// open-interval indemnity ran 7.1× what settled. So:
//
//   · CLOSED or FINAL: the index (rain ÷ full normal) is the real number, and a
//     dollar figure may follow from it.
//   · OPEN: the only number is percent of normal TO DATE — rain so far ÷ the
//     normal for the same days. No index, no trigger call, no dollars.
//   · UPCOMING: nothing.
//
// The interval's calendar is the POLICY's crop year, never today's calendar
// year, so a 2026 policy viewed in January 2027 reads closed, not upcoming.

const INTERVAL_MONTHS = {
  JANFEB: [0, 1], FEBMAR: [1, 2], MARAPR: [2, 3], APRMAY: [3, 4],
  MAYJUN: [4, 5], JUNJUL: [5, 6], JULAUG: [6, 7], AUGSEP: [7, 8],
  SEPOCT: [8, 9], OCTNOV: [9, 10], NOVDEC: [10, 11], DECJAN: [11, 0],
};
const DAY = 86400000;

// [start, end] as UTC midnights, both inclusive days, for one crop year.
function intervalWindow(code, cropYear) {
  const mr = INTERVAL_MONTHS[code];
  const yr = Number(cropYear);
  if (!mr || !Number.isFinite(yr)) return null;
  // DECJAN (not an interval RMA publishes) runs December of the prior year into
  // January, as it always has on this screen.
  const startYr = code === "DECJAN" ? yr - 1 : yr;
  const endYr = code === "DECJAN" ? yr : yr;
  const start = Date.UTC(startYr, mr[0], 1);
  const end = Date.UTC(endYr, mr[1] + 1, 0); // last day of the second month
  return { start, end, days: Math.round((end - start) / DAY) + 1 };
}

// "upcoming" | "open" | "closed" | null. An interval is closed from the day
// after its last day. FINAL from RMA is always closed.
function intervalStatus(code, cropYear, { isFinal = false, now = Date.now() } = {}) {
  if (isFinal) return "closed";
  const w = intervalWindow(code, cropYear);
  if (!w) return null;
  const t = typeof now === "number" ? now : new Date(now).getTime();
  if (t < w.start) return "upcoming";
  if (t >= w.end + DAY) return "closed";
  return "open";
}

// Share of the interval elapsed, 0..1, on the crop year's calendar.
function elapsedFraction(code, cropYear, now = Date.now()) {
  const w = intervalWindow(code, cropYear);
  if (!w) return null;
  const t = typeof now === "number" ? now : new Date(now).getTime();
  if (t < w.start) return 0;
  if (t >= w.end + DAY) return 1;
  return (t - w.start) / (w.end + DAY - w.start);
}

const num = (v) => (v != null && v !== "" && Number.isFinite(Number(v)) ? Number(v) : null);

// Normal rainfall (inches) for the days the estimate covers. The sync's own
// figure (normal_to_date_inches, climatology-weighted) when present; otherwise
// the interval's normal scaled by days covered through estimated_as_of.
function normalToDate(code, cropYear, { normalInches, normalToDateInches, asOf } = {}) {
  const own = num(normalToDateInches);
  if (own != null && own > 0) return own;
  const full = num(normalInches);
  const w = intervalWindow(code, cropYear);
  if (full == null || full <= 0 || !w || !asOf) return null;
  const through = Date.parse(`${String(asOf).slice(0, 10)}T00:00:00Z`);
  if (!Number.isFinite(through) || through < w.start) return null;
  const covered = Math.min(w.days, Math.round((through - w.start) / DAY) + 1);
  return full * (covered / w.days);
}

// Percent of normal to date for an OPEN interval: rain so far ÷ normal for the
// same days, × 100. Null when either side is missing.
function pctOfNormalToDate(code, cropYear, { estimatedInches, normalInches, normalToDateInches, asOf } = {}) {
  const rain = num(estimatedInches);
  const denom = normalToDate(code, cropYear, { normalInches, normalToDateInches, asOf });
  if (rain == null || denom == null || denom <= 0) return null;
  return (rain / denom) * 100;
}

// What one grid-interval may show. `index` is the reading a trigger and dollars
// may be judged on (null while open). `toDate` is the open-interval reading.
// `dollarLabel` names the dollars, or is null when none may show.
function intervalReading(code, cropYear, entry, { now = Date.now() } = {}) {
  const e = entry || {};
  const finalVal = num(e.finalValue);
  const isFinal = finalVal != null;
  const status = intervalStatus(code, cropYear, { isFinal, now });
  // A closed interval whose rain total does not yet reach its last day (the
  // daily feed runs a day or two behind) is still short: rain ÷ full normal
  // would read low and overstate any payment. Until the total covers the whole
  // interval it reads to date, with no dollars.
  const w = intervalWindow(code, cropYear);
  const asOfT = e.estimatedAsOf ? Date.parse(`${String(e.estimatedAsOf).slice(0, 10)}T00:00:00Z`) : null;
  const short = !isFinal && w && Number.isFinite(asOfT) && asOfT < w.end;
  if (status === "closed" && short) {
    const toDate = pctOfNormalToDate(code, cropYear, {
      estimatedInches: e.estimatedInches, normalInches: e.normalInches,
      normalToDateInches: e.normalToDateInches, asOf: e.estimatedAsOf,
    });
    return { status, index: null, toDate, source: toDate != null ? "TO_DATE" : null, dollarLabel: null };
  }
  if (status === "closed") {
    const index = isFinal ? finalVal : num(e.estimatedValue);
    return { status, index, toDate: null, source: isFinal ? "FINAL" : (index != null ? "ESTIMATED" : null),
             dollarLabel: index == null ? null : (isFinal ? "Final" : "Estimate at close") };
  }
  if (status === "open") {
    const toDate = pctOfNormalToDate(code, cropYear, {
      estimatedInches: e.estimatedInches, normalInches: e.normalInches,
      normalToDateInches: e.normalToDateInches, asOf: e.estimatedAsOf,
    });
    return { status, index: null, toDate, source: toDate != null ? "TO_DATE" : null, dollarLabel: null };
  }
  return { status, index: null, toDate: null, source: null, dollarLabel: null };
}

module.exports = {
  INTERVAL_MONTHS, intervalWindow, intervalStatus, elapsedFraction,
  normalToDate, pctOfNormalToDate, intervalReading,
};
